import { type ReactNode, useMemo } from "react";

import { methodClass } from "../lib/methods.js";
import { useTheme } from "../lib/theme.jsx";
import Mermaid from "./Mermaid.jsx";

// Cores por verbo dentro do diagrama. O mermaid não lê variáveis CSS, então os
// valores entram literais — e precisam de um par por tema: no escuro as cores
// clareiam e o texto do nó inverte, senão fica escuro sobre escuro.
const METHOD_FILL = {
  light: { GET: "#0F9D58", POST: "#2563EB", PUT: "#D97706", DELETE: "#B4231E", PATCH: "#D97706" },
  dark: { GET: "#3FBF83", POST: "#6E9BFF", PUT: "#E3A344", DELETE: "#F2726C", PATCH: "#E3A344" },
};
const NODE_TEXT = { light: "#1A1F30", dark: "#E9EBF0" };

// <Flow> recebe os passos como dados e monta o mermaid sozinho. Escrever o
// diagrama à mão significaria manter duas listas em sincronia — a do desenho e a
// da legenda embaixo — e elas divergem no primeiro endpoint que mudar de nome.
/** Um passo do diagrama: uma chamada e, opcionalmente, o rótulo da seta. */
interface FlowStep {
  method: string;
  path: string;
  desc?: string;
  edge?: string;
}

interface FlowProps {
  title?: ReactNode;
  desc?: ReactNode;
  steps?: FlowStep[];
}

export default function Flow({ title, desc, steps = [] }: FlowProps) {
  const { theme } = useTheme();

  const chart = useMemo(() => {
    const fills = METHOD_FILL[theme];
    const text = NODE_TEXT[theme];
    const lines = ["graph TD"];
    steps.forEach((s, i) => {
      const label = `${s.method} ${s.path}`.replace(/"/g, "'");
      lines.push(`  n${i}["${label}"]:::${s.method}`);
      if (i > 0) {
        const edge = (steps[i - 1]?.edge || "").replace(/"/g, "'");
        lines.push(edge ? `  n${i - 1} -->|${edge}| n${i}` : `  n${i - 1} --> n${i}`);
      }
    });
    for (const [method, color] of Object.entries(fills)) {
      lines.push(
        `  classDef ${method} fill:${color}1a,stroke:${color},stroke-width:1.5px,color:${text},font-size:13px,rx:6,ry:6;`
      );
    }
    return lines.join("\n");
  }, [steps, theme]);

  return (
    <div className="flow-card">
      {title && <div className="flow-card-title">{title}</div>}
      {desc && <p className="doc-p">{desc}</p>}
      <Mermaid chart={chart} />
      <ol className="flow-legend">
        {steps.map((s, i) => (
          <li key={i}>
            <span className={`ref-method ${methodClass(s.method)}`}>{s.method}</span>
            <code className="mono">{s.path}</code>
            <span className="flow-legend-desc">{s.desc}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
